import { useEffect, useRef, useState } from 'react'
import { Select } from './Select.tsx'
import { Input } from './Input.tsx'
import { icons } from './icons.ts'

/**
 * One choice out of a list too long to scroll.
 *
 * `MultiSelect` already answers "several out of many, with a search box", and
 * this is the same control for the case where exactly one answer is wanted --
 * a model out of the four hundred and forty-six OpenRouter serves. A plain
 * `<select>` there is a scrollbar and a squint.
 *
 * Built on `Select`'s `customDropdown`, which is what that prop is for, so the
 * trigger, the chevron, the focus ring, the sizing and the label all come from
 * the same component every other field on every other screen uses. The only
 * thing added here is the panel: a search box and a filtered list.
 *
 * The matching rule is `MultiSelect`'s deliberately -- a plain
 * case-insensitive substring over the whole label -- because two controls that
 * filter differently is a surprise nobody needs, and a model should be found
 * by typing either its vendor or its name.
 */
export function SearchableSelect({
  label,
  options,
  value,
  onChange,
  placeholder = 'Escolha uma opção',
  disabled = false,
  size = 'small',
}: {
  label?: string
  options: readonly string[]
  value: string
  onChange: (next: string) => void
  placeholder?: string
  disabled?: boolean
  size?: 'small' | 'medium'
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const search = useRef<HTMLInputElement>(null)

  // Opening lands the cursor in the search box, because with a list this long
  // the first thing anybody does is type.
  useEffect(() => {
    if (open) search.current?.focus()
    else setQuery('')
  }, [open])

  const normalised = query.trim().toLowerCase()
  const shown =
    normalised === '' ? options : options.filter((o) => o.toLowerCase().includes(normalised))

  return (
    <Select
      size={size}
      disabled={disabled}
      {...(label === undefined ? {} : { label })}
      open={open}
      onOpenChange={setOpen}
      placeholder={placeholder}
      {...(value === '' ? {} : { displayValue: value })}
      dropdownClassName="rounded-base border-stroke-weak bg-background-raised border shadow-lg"
      customDropdown={({ close }) => (
        <div className="flex max-h-72 flex-col">
          <div className="border-stroke-weak border-b p-2">
            <Input
              ref={search}
              size="small"
              leadingIcon={icons.search}
              placeholder="Filtrar…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="overflow-auto p-1">
            {shown.length === 0 ? (
              <p className="text-tSmall text-text-weak p-2">Nada encontrado.</p>
            ) : (
              shown.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`text-tSmall hover:bg-fill-weak block w-full truncate rounded px-2 py-1.5 text-left ${
                    option === value ? 'text-text-brand font-semibold' : 'text-text-strong'
                  }`}
                  onClick={() => {
                    onChange(option)
                    close()
                  }}
                >
                  {option}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    />
  )
}
